import React, { useCallback, useEffect, useState } from "react";
import { LicenceCheckingService } from "services";
import { NotificationToaster } from "components/notifications";
import { FormNumericInput, FormSelect } from "components/form-fields";
import { Intent } from "@blueprintjs/core";
import { ActionPanel } from "components/panel";
import { DataDisplayRecord } from "components/data-display";
import { ButtonSave } from "components/buttons";
import { GridLayout } from "components/grid";
import { useValidation } from "hooks/useValidation";
import PropTypes from "prop-types";

export function IncompleteVehicleRegistrationPanel({
    registration,
    checkId,
    missingPolicyInfo,
    policyRules,
    reloadData,
}) {
    const [isValid, errors, validate] = useValidation();
    const [isSaving, setIsSaving] = useState(false);

    const [missingData, setMissingData] = useState(
        (missingPolicyInfo || []).map((rule) => ({
            missingDataKey: rule,
            missingDataValue: 0,
        }))
    );

    useEffect(() => {
        const rules = [];
        let externalRules = [];

        missingData.forEach((d) => {
            const reason = (policyRules || []).find(
                (r) => r.missingDataKey === d.missingDataKey
            );

            if (reason?.missingDataType !== "VEHICLE") {
                return;
            }

            if (reason.ruleType === "MULTISELECT") {
                externalRules.push({ fieldName: d.missingDataKey, valid: d.missingDataValue !== 0, errorMessage: `${reason.missingDataName} is required` });
            }

            if (reason.ruleType === "NUMBER") {
                if (reason.missingDataKey === "YEAROFMANUFACTURE") {
                    const currentYear = new Date().getFullYear();
                    const minYear = currentYear - reason.max;
                    const maxYear = currentYear - reason.min;
                    externalRules.push({ fieldName: d.missingDataKey, valid: d.missingDataValue >= minYear && d.missingDataValue <= maxYear, errorMessage: `${reason.missingDataName} must be between ${minYear} and ${maxYear}` });
                }
                else {
                    externalRules.push({ fieldName: d.missingDataKey, valid: d.missingDataValue >= reason.min && d.missingDataValue <= reason.max, errorMessage: `${reason.missingDataName} must be between ${reason.min} ${reason.suffix || ""} and ${reason.max} ${reason.suffix || ""}` });
                }
            }
        });
        validate(rules, missingData, externalRules);
    }, [missingData]);

    const missingElements = (missingPolicyInfo || []).map((missingKey) => {
        const reason = (policyRules || []).find(
            (r) => r.missingDataKey === missingKey
        );
        const policyValue = missingData.find(
            (d) => d.missingDataKey === missingKey
        );
        if (reason && reason.missingDataType === "VEHICLE") {
            return renderParameterField(reason, policyValue?.missingDataValue);
        }
        return null;
    });

    function renderParameterField(option, value) {
        switch (option.ruleType) {
            case "NUMBER":
                if (option.missingDataKey === "YEAROFMANUFACTURE") {
                    const currentYear = new Date().getFullYear();
                    const minYear = currentYear - option.max;
                    const maxYear = currentYear - option.min;
                    return (
                        <div key={`${option.key}-grey-fleet-policy-value`}>
                            <FormNumericInput
                                headingText={option.missingDataName}
                                min={minYear}
                                max={maxYear}
                                selectedValue={value}
                                onValueChange={(newValue) =>
                                    handleOptionValueChange(option, newValue)
                                }
                                dangerHelperText={errors[option.missingDataKey]}
                            />
                        </div>
                    )
                }
                else {
                    return (
                        <div key={`${option.key}-grey-fleet-policy-value`}>
                            <FormNumericInput
                                headingText={option.missingDataName}
                                min={option.min}
                                max={option.max}
                                selectedValue={value}
                                onValueChange={(newValue) =>
                                    handleOptionValueChange(option, newValue)
                                }
                                dangerHelperText={errors[option.missingDataKey]}
                                unit={option.suffix}
                            />
                        </div>
                    );
                }
            case "MULTISELECT":
                return (
                    <div key={`${option.key}-grey-fleet-policy-multiselect`}>
                        <FormSelect
                            headingText={option.missingDataName}
                            disabled={isSaving || value === undefined}
                            items={option.options}
                            onItemSelect={(item) =>
                                handleOptionValueChange(option, item.id)
                            }
                            selectedValue={value}
                                dangerHelperText={errors[option.missingDataKey]}
                        />
                    </div>
                );
            default:
                return <span key={option.key}>-</span>;
        }
    }

    const handleSave = useCallback(() => {
        setIsSaving(true);
        LicenceCheckingService.addMissingVehicleDetails(checkId, missingData)
            .then(
                () => {
                    NotificationToaster.show(
                        Intent.SUCCESS,
                        "Missing vehicle details saved successfully"
                    );
                    reloadData();
                },
                () => {
                    NotificationToaster.show(
                        Intent.DANGER,
                        "Could not save missing vehicle details"
                    );
                }
            )
            .catch(() => {
                NotificationToaster.show(
                    Intent.DANGER,
                    "Could not save missing vehicle details"
                );
            })
            .finally(() => {
                setIsSaving(false);
            });
    }, [checkId, missingData, reloadData]);

    const handleOptionValueChange = useCallback((option, newValue) => {
        setMissingData((prev) =>
            prev.map((p) =>
                p.missingDataKey === option.missingDataKey
                    ? { ...p, missingDataValue: newValue }
                    : p
            )
        );
    }, []);

    return (
        <div className="spacer-top">
            <ActionPanel
                intent="warning"
                title="Incomplete Vehicle Registration"
            >
                <div>
                    <p className="spacer-bottom">
                        The vehicle for this check has an incomplete or partial
                        record with the DVLA. This information is required to
                        pass the Grey Fleet Check policy. Please fill out the
                        missing information below.
                    </p>
                    <DataDisplayRecord
                        header="Vehicle Registration"
                        data={registration}
                    />
                    <div className="spacer-top">
                        <GridLayout numberOfColumns={3} gap={30}>
                            {missingElements}
                        </GridLayout>
                    </div>
                    <div>
                        <div className="bp6-dialog-footer-actions">
                            <ButtonSave
                                onClick={handleSave}
                                disabled={isSaving}
                                simpleDisabled={!isValid || missingData.length === 0}
                            />
                        </div>
                    </div>
                </div>
            </ActionPanel>
        </div>
    );
}

IncompleteVehicleRegistrationPanel.propTypes = {
    registration: PropTypes.string.isRequired,
    checkId: PropTypes.number.isRequired,
    missingPolicyInfo: PropTypes.array.isRequired,
    policyRules: PropTypes.array.isRequired,
    reloadData: PropTypes.func.isRequired,
};
